import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
} from '@mui/material';
import {
  Reddit as RedditIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  getSubredditFromUrl,
  adminPath,
  trendsPath,
  navigateOrExternal,
  marketingHomeUrl,
  displayImageSourceForCfbFlairWizard,
  matchesFlairTemplateSearch,
} from '../utils/subreddit';

interface FlairTemplate {
  id: string;
  class_name: string;
  family_name: string;
  genus_name: string;
  flair_name: string;
  /** Per-image label (wiki / chat); may differ from flair_name when alternates exist */
  image_name?: string | null;
  text: string;
  abbreviated: string;
  css_class: string;
  flair_code: string;
  image_source: string;
  is_default: boolean;
  user_count?: number;
  class_image?: string | null;
  family_image?: string | null;
  genus_image?: string | null;
  is_award?: boolean;
  has_earned?: boolean;
  class_order?: number;
  family_order?: number;
  genus_order?: number;
  /** From flair_stats when present — tie-break when user_count matches */
  percent_change?: number;
}

/** Trending: higher % first; same % → more current users wins (e.g. 3→7 before 2→5 at 100%). */
function compareTrendingFlairs(
  a: { percent_change?: number; current_count?: number; id?: string },
  b: { percent_change?: number; current_count?: number; id?: string }
): number {
  const pa = Number(a.percent_change ?? 0);
  const pb = Number(b.percent_change ?? 0);
  if (Math.abs(pa - pb) > 1e-6) return pb - pa;
  const ca = Number(a.current_count ?? 0);
  const cb = Number(b.current_count ?? 0);
  if (cb !== ca) return cb - ca;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

/** Popular (by usage): most users first; same count → higher % growth, then stable label order. */
function comparePopularFlairs(a: FlairTemplate, b: FlairTemplate): number {
  const ua = Number(a.user_count ?? 0);
  const ub = Number(b.user_count ?? 0);
  if (ub !== ua) return ub - ua;
  const pa = Number(a.percent_change ?? 0);
  const pb = Number(b.percent_change ?? 0);
  if (Math.abs(pa - pb) > 1e-6) return pb - pa;
  return (a.text || '').localeCompare(b.text || '');
}

/** 12 flair tiles: phone = 4×3 (3 cols), tablet/desktop = 3×4 (4 cols) */
const POPULAR_TRENDING_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(3, minmax(0, 1fr))',
    sm: 'repeat(4, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  gap: 0.5,
  width: '100%',
  minWidth: 0,
};

const MAIN_FLAIR_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(3, minmax(0, 1fr))',
    sm: 'repeat(4, minmax(0, 1fr))',
    md: 'repeat(5, minmax(0, 1fr))',
    lg: 'repeat(6, minmax(0, 1fr))',
  },
  gap: 2,
  justifyContent: 'center',
  width: '100%',
  minWidth: 0,
};

const SubredditPage: React.FC = () => {
  const { subreddit: rawSubreddit } = useParams<{ subreddit: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const { user, login, logout, isLoading: authLoading, checkModeratorStatus, clearModeratorStatus } = useAuth();
  
  // Get subreddit from URL (subdomain or path) or from route params
  const subredditFromUrl = getSubredditFromUrl();
  const subreddit = (rawSubreddit?.toLowerCase() || subredditFromUrl) || '';
  const canonicalSubredditName = rawSubreddit || subredditFromUrl || '';
  const canSelectFlair = Boolean(user);
  
  const [isCheckingModerator, setIsCheckingModerator] = useState<boolean>(false);
  const [flairTemplates, setFlairTemplates] = useState<FlairTemplate[]>([]);
  /** When this sub subscribes to a parent for flair data, CDN paths use the parent's sub name. */
  const [flairDataSourceSub, setFlairDataSourceSub] = useState<string | null>(null);
  const [isLoadingFlair, setIsLoadingFlair] = useState<boolean>(true);
  const [checkedSubreddit, setCheckedSubreddit] = useState<string | null>(null);
  
  // Flair selection state
  const [primaryFlair, setPrimaryFlair] = useState<FlairTemplate | null>(null);
  const [secondaryFlair, setSecondaryFlair] = useState<FlairTemplate | null>(null);
  const [activeSlot, setActiveSlot] = useState<'primary' | 'secondary'>('primary');
  const [isUpdating, setIsUpdating] = useState(false);
  const [userFlairData, setUserFlairData] = useState<{ protect_text: number; flair_text: string } | null>(null);
  const [protectedFlairText, setProtectedFlairText] = useState('');
  const [trendingFlairs, setTrendingFlairs] = useState<any[]>([]);
  const [isLoadingTrending, setIsLoadingTrending] = useState(false);
  
  // Navigation state for hierarchical browsing
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [selectedGenus, setSelectedGenus] = useState<string | null>(null);
  
  // Search state (debounced so typing does not filter thousands of rows every keystroke)
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const flairTemplatesLoadSeqRef = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (subreddit && user) {
      if (checkedSubreddit !== subreddit) {
        setCheckedSubreddit(subreddit);
        clearModeratorStatus();
        checkModeratorStatusForSubreddit();
      }
    }
    if (!subreddit || authLoading) return;
    setFlairDataSourceSub(null);
    loadFlairTemplates();
    loadTrendingFlairs();
    // Wait for auth hydration; username + Authorization drive award_users (has_earned)
  }, [subreddit, authLoading, user?.username]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const flairByCodeLower = useMemo(() => {
    const m = new Map<string, FlairTemplate>();
    for (const t of flairTemplates) {
      const c = (t.flair_code || '').trim().toLowerCase();
      if (c) m.set(c, t);
    }
    return m;
  }, [flairTemplates]);

  // Load user flair data after templates are loaded
  useEffect(() => {
    if (subreddit && user && flairTemplates.length > 0) {
      loadUserFlairData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subreddit, user?.id, flairTemplates.length]);

  const loadTrendingFlairs = async () => {
    if (!subreddit) return;
    setIsLoadingTrending(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/trending-flairs/${subreddit}?limit=12`);
      
      if (response.ok) {
        const data = await response.json();
        const list = data.trending || [];
        setTrendingFlairs([...list].sort(compareTrendingFlairs));
        if (typeof data.data_source_sub === 'string' && data.data_source_sub) {
          setFlairDataSourceSub(data.data_source_sub);
        }
      }
    } catch (error) {
      console.error('Error loading trending flairs:', error);
    } finally {
      setIsLoadingTrending(false);
    }
  };

  const loadUserFlairData = async () => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/user-flair/${subreddit}`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.flair) {
          setUserFlairData(data.flair);
          // Extract protected text (remove emoji prefix)
          if (data.flair.protect_text && data.flair.flair_text) {
            const text = data.flair.flair_text;
            // Remove all emoji patterns from the start (e.g., :mickey: :donald: or :mickey::donald:)
            // Match all emojis at the start (greedy) followed by optional space
            const textWithoutEmojis = text.replace(/^(:[^:]+:\s*)+/, '').trim();
            setProtectedFlairText(textWithoutEmojis);
          }
          
            // Set primary and secondary flairs based on flair1/flair2
            // flair1 and flair2 should now contain flair_code (e.g., "stanford", "augustanational")
            // not the CSS class with coordinates (e.g., "stanford-sheet1-row02-col12")
            if (data.flair.flair1 || data.flair.flair2 || data.flair.flair_css) {
              // Find matching flairs from templates by matching flair_code first (most reliable)
              const findFlairByValue = (value: string | null) => {
                if (!value) return null;
                const v = value.trim();
                const vl = v.toLowerCase();

                let found = flairByCodeLower.get(vl);
                if (found) return found;

                found = flairTemplates.find((t) => t.flair_code === v);
                if (found) return found;

                found = flairTemplates.find((t) => t.css_class === v);
                if (found) return found;

                const valueParts = v.split('-');
                if (valueParts.length > 0) {
                  const baseCode = valueParts[0].toLowerCase();
                  found = flairByCodeLower.get(baseCode);
                  if (found) return found;
                  found = flairTemplates.find((t) => t.css_class?.includes(valueParts[0]));
                  if (found) return found;
                }

                found = flairTemplates.find(
                  (t) => t.css_class && (t.css_class.includes(v) || v.includes(t.css_class))
                );
                return found ?? null;
              };
            
            let primary = null;
            let secondary = null;
            
            // Try to find by flair1 first
            if (data.flair.flair1) {
              primary = findFlairByValue(data.flair.flair1);
            }
            
            // If not found and we have flair_css, try parsing it
            if (!primary && data.flair.flair_css) {
              const cssParts = data.flair.flair_css.split('-');
              if (cssParts.length > 0) {
                primary = findFlairByValue(cssParts[0]);
              }
            }
            
            // Try to find secondary by flair2
            if (data.flair.flair2) {
              secondary = findFlairByValue(data.flair.flair2);
            }
            
            // If not found and we have flair_css with multiple parts
            if (!secondary && data.flair.flair_css) {
              const cssParts = data.flair.flair_css.split('-');
              if (cssParts.length > 1) {
                secondary = findFlairByValue(cssParts[1]);
              }
            }

            if (
              primary &&
              secondary &&
              primary.flair_code != null &&
              secondary.flair_code != null &&
              primary.flair_code.toLowerCase() === secondary.flair_code.toLowerCase()
            ) {
              secondary = null;
            }
            
            setPrimaryFlair(primary);
            setSecondaryFlair(secondary);
          } else {
            setPrimaryFlair(null);
            setSecondaryFlair(null);
          }
        } else {
          // No flair set
          setPrimaryFlair(null);
          setSecondaryFlair(null);
        }
      }
    } catch (error) {
      console.error('Error loading user flair data:', error);
    }
  };

  const checkModeratorStatusForSubreddit = async () => {
    if (!subreddit || !user) return;
    setIsCheckingModerator(true);
    try {
      await checkModeratorStatus(subreddit);
    } catch (error) {
      console.error('Error checking moderator status:', error);
    } finally {
      setIsCheckingModerator(false);
    }
  };

  const loadFlairTemplates = async () => {
    if (!subreddit || authLoading) return;
    const seq = ++flairTemplatesLoadSeqRef.current;
    setIsLoadingFlair(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const headers: Record<string, string> = {};
      if (user?.username) {
        headers.Authorization = `Bearer ${user.username}`;
      }
      const usernameParam = user?.username ? `?username=${encodeURIComponent(user.username)}` : '';
      const response = await fetch(`${API_BASE_URL}/flair-templates/${subreddit}${usernameParam}`, {
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        if (seq !== flairTemplatesLoadSeqRef.current) return;
        setFlairTemplates(data.templates || []);
        if (typeof data.data_source_sub === 'string' && data.data_source_sub) {
          setFlairDataSourceSub(data.data_source_sub);
        }
      }
    } catch (error) {
      console.error('Error loading flair templates:', error);
    } finally {
      if (seq === flairTemplatesLoadSeqRef.current) {
        setIsLoadingFlair(false);
      }
    }
  };

  // Unique class / family / genus in master_sheet order (API returns rows ordered by class_order, sheet, etc.)
  const classes = useMemo((): Array<{ name: string; image: string | null }> => {
    const out: Array<{ name: string; image: string | null }> = [];
    const seen = new Set<string>();
    for (const t of flairTemplates) {
      if (!seen.has(t.class_name)) {
        seen.add(t.class_name);
        out.push({ name: t.class_name, image: t.class_image || null });
      }
    }
    return out;
  }, [flairTemplates]);

  const families = useMemo((): Array<{ name: string; image: string | null }> => {
    if (!selectedClass) return [];
    const out: Array<{ name: string; image: string | null }> = [];
    const seen = new Set<string>();
    for (const t of flairTemplates) {
      if (t.class_name !== selectedClass) continue;
      if (!seen.has(t.family_name)) {
        seen.add(t.family_name);
        out.push({ name: t.family_name, image: t.family_image || null });
      }
    }
    return out;
  }, [flairTemplates, selectedClass]);

  const genuses = useMemo((): Array<{ name: string; image: string | null }> => {
    const out: Array<{ name: string; image: string | null }> = [];
    const seen = new Set<string>();
    let filteredTemplates = flairTemplates;
    if (selectedClass) {
      filteredTemplates = filteredTemplates.filter((t) => t.class_name === selectedClass);
    }
    if (selectedFamily) {
      filteredTemplates = filteredTemplates.filter((t) => t.family_name === selectedFamily);
    }
    for (const t of filteredTemplates) {
      if (!seen.has(t.genus_name)) {
        seen.add(t.genus_name);
        out.push({ name: t.genus_name, image: t.genus_image || null });
      }
    }
    return out;
  }, [flairTemplates, selectedClass, selectedFamily]);

  // Auto-advance navigation when there's only one option (but still show genuses)
  useEffect(() => {
    if (selectedClass && families.length === 1 && !selectedFamily) {
      setSelectedFamily(families[0].name);
    }
  }, [selectedClass, families, selectedFamily]);

  // Don't auto-advance genuses - always show them so user can see all options

  // Filter flairs based on navigation and search
  const filteredFlairs = useMemo(() => {
    let filtered = flairTemplates;

    // Apply hierarchical filters
    if (selectedGenus) {
      // If genus is selected, filter by genus (most specific)
      filtered = filtered.filter(t => t.genus_name === selectedGenus);
    } else if (selectedFamily) {
      // If family is selected but not genus, filter by family
      filtered = filtered.filter(t => t.family_name === selectedFamily);
      if (selectedClass) {
        filtered = filtered.filter(t => t.class_name === selectedClass);
      }
    } else if (selectedClass) {
      // If only class is selected, filter by class
      filtered = filtered.filter(t => t.class_name === selectedClass);
    }

    if (debouncedSearch) {
      filtered = filtered.filter((t) => matchesFlairTemplateSearch(t, debouncedSearch));
    }

    return filtered;
  }, [flairTemplates, selectedClass, selectedFamily, selectedGenus, debouncedSearch]);

  const sameFlairCode = (a: FlairTemplate | null, b: FlairTemplate | null) =>
    Boolean(
      a &&
        b &&
        a.flair_code != null &&
        b.flair_code != null &&
        a.flair_code.toLowerCase() === b.flair_code.toLowerCase()
    );

  /** Block picking a flair whose flair_code matches the other slot (may differ by image). */
  const isFlairBlockedDuplicate = (flair: FlairTemplate) => {
    if (activeSlot === 'primary') {
      return sameFlairCode(secondaryFlair, flair);
    }
    return sameFlairCode(primaryFlair, flair);
  };

  const handleFlairClick = (flair: FlairTemplate) => {
    // If clicking the same flair that's already in the active slot, deselect it
    if (activeSlot === 'primary' && primaryFlair?.id === flair.id) {
      setPrimaryFlair(null);
    } else if (activeSlot === 'secondary' && secondaryFlair?.id === flair.id) {
      setSecondaryFlair(null);
    } else {
      if (activeSlot === 'primary') {
        if (sameFlairCode(secondaryFlair, flair)) {
          setSecondaryFlair(null);
        }
        setPrimaryFlair(flair);
        setActiveSlot('secondary');
      } else {
        if (sameFlairCode(primaryFlair, flair)) {
          setSecondaryFlair(null);
          setActiveSlot('primary');
          return;
        }
        setSecondaryFlair(flair);
        setActiveSlot('primary');
      }
    }
  };

  const [siblingDialogOpen, setSiblingDialogOpen] = useState(false);
  const [siblingTargets, setSiblingTargets] = useState<
    {
      subreddit: string;
      cdn_sub?: string;
      displayName: string;
      logoUrl: string | null;
      primaryFlairId: string | null;
      secondaryFlairId: string | null;
    }[]
  >([]);
  const [siblingChecked, setSiblingChecked] = useState<Record<string, boolean>>({});
  const [isApplyingSiblings, setIsApplyingSiblings] = useState(false);

  const handleUpdateFlair = async () => {
    if (!user || (!primaryFlair && !secondaryFlair)) return;

    setIsUpdating(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/set-flair/${subreddit}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({
          primaryFlairId: primaryFlair?.id,
          secondaryFlairId: secondaryFlair?.id,
          protect_text: userFlairData?.protect_text === 1,
          flair_text: userFlairData?.protect_text === 1 ? protectedFlairText : undefined,
        }),
      });

      if (response.ok) {
        await response.json();
        await loadUserFlairData();

        const q = new URLSearchParams();
        if (primaryFlair?.id) q.set('primaryFlairId', primaryFlair.id);
        if (secondaryFlair?.id) q.set('secondaryFlairId', secondaryFlair.id);
        const mRes = await fetch(`${API_BASE_URL}/flair-sibling-matches/${subreddit}?${q.toString()}`, {
          headers: { Authorization: `Bearer ${user.username}` },
        });
        if (mRes.ok) {
          const mData = (await mRes.json()) as { targets?: typeof siblingTargets };
          const list = mData.targets ?? [];
          if (list.length > 0) {
            const init: Record<string, boolean> = {};
            list.forEach((t) => {
              init[t.subreddit] = true;
            });
            setSiblingTargets(list);
            setSiblingChecked(init);
            setSiblingDialogOpen(true);
          }
        }
      }
    } catch (error) {
      console.error('Error updating flair:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApplySiblingFlairs = async () => {
    if (!user || siblingTargets.length === 0) return;
    const { API_BASE_URL } = await import('../config');
    const targets = siblingTargets
      .filter((t) => siblingChecked[t.subreddit])
      .map((t) => ({
        subreddit: t.subreddit,
        primaryFlairId: t.primaryFlairId,
        secondaryFlairId: t.secondaryFlairId,
      }));
    if (targets.length === 0) {
      setSiblingDialogOpen(false);
      return;
    }
    setIsApplyingSiblings(true);
    try {
      await fetch(`${API_BASE_URL}/apply-flair-sibling-subs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ targets }),
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsApplyingSiblings(false);
      setSiblingDialogOpen(false);
    }
  };

  const handleResetFlair = () => {
    setPrimaryFlair(null);
    setSecondaryFlair(null);
  };

  const handleNavigationClick = (level: 'class' | 'family' | 'genus', value: string | null) => {
    if (level === 'class') {
      setSelectedClass(value);
      setSelectedFamily(null);
      setSelectedGenus(null);
    } else if (level === 'family') {
      setSelectedFamily(value);
      setSelectedGenus(null);
    } else if (level === 'genus') {
      setSelectedGenus(value);
    }
    // Reset to first page when navigation changes
    setCurrentPage(1);
  };
  
  // Paginated flairs
  const paginatedFlairs = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredFlairs.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredFlairs, currentPage]);
  
  const totalPages = Math.ceil(filteredFlairs.length / ITEMS_PER_PAGE);

  const getImageUrl = (imageSource: string, subredditName?: string) => {
    const cdnBaseUrl = import.meta.env.VITE_CDN_BASE_URL || 'https://cdn.redditcfb.com';
    const imagePath = import.meta.env.VITE_CDN_IMAGE_PATH || '60x60';
    const sub = subredditName || flairDataSourceSub || subreddit;
    const stem = displayImageSourceForCfbFlairWizard(imageSource);
    // image_source is now just the image name (e.g., "gandalf"), subreddit is prepended
    return `${cdnBaseUrl}/${imagePath}/${sub}/${stem}.png`;
  };

  if (!subreddit) {
    return (
      <Container>
        <Alert severity="error">Invalid subreddit</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary" sx={{ flexShrink: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigateOrExternal(navigate, marketingHomeUrl())}
            sx={{ mr: 2 }}
            aria-label="Back to FlairWizard home"
          >
            <ArrowBackIcon />
          </IconButton>
          <RedditIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            /r/{canonicalSubredditName} Flair
          </Typography>
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isCheckingModerator ? (
                <CircularProgress size={20} color="inherit" />
              ) : user.isModerator ? (
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#ff4500', '&:hover': { backgroundColor: '#e03e00' } }}
                  startIcon={<AdminIcon />}
                  onClick={() => navigateOrExternal(navigate, adminPath(canonicalSubredditName))}
                  size="small"
                >
                  Manage Flair
                </Button>
              ) : null}
              <Typography variant="body2">
                {user.username}
              </Typography>
              <Button
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={logout}
                size="small"
              >
                Logout
              </Button>
            </Box>
          ) : (
            <Button
              color="inherit"
              startIcon={<LoginIcon />}
              onClick={login}
              disabled={authLoading}
            >
              Login with Reddit
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          <>
            {user && (
              <>
              {/* Selected Flair Display */}
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Your Flair Selection
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, justifyContent: 'center' }}>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 8px)' }, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      onClick={() => setActiveSlot('primary')}
                      sx={{
                        border: '3px solid',
                        borderColor: activeSlot === 'primary' 
                          ? 'success.main' 
                          : primaryFlair 
                            ? 'text.primary' 
                            : 'grey.300',
                        borderRadius: 2,
                        p: 1,
                        height: 140,
                        width: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: activeSlot === 'primary' ? 'success.dark' : 'primary.main',
                        },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontSize: '0.7rem' }}>
                        Primary Flair
                      </Typography>
                      {primaryFlair ? (
                        <>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 0.5,
                            }}
                          >
                            <img
                              src={getImageUrl(primaryFlair.image_source)}
                              alt={primaryFlair.text}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              textAlign: 'center',
                              fontSize: '0.65rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              minHeight: '2.4em',
                              width: '100%',
                            }}
                          >
                            {primaryFlair.text}
                          </Typography>
                        </>
                      ) : (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            minHeight: '2.4em', 
                            display: 'flex', 
                            alignItems: 'center',
                            textAlign: 'center',
                            fontSize: '0.65rem',
                            width: '100%',
                          }}
                        >
                          No flair selected
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 8px)' }, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      onClick={() => setActiveSlot('secondary')}
                      sx={{
                        border: '3px solid',
                        borderColor: activeSlot === 'secondary' 
                          ? 'success.main' 
                          : secondaryFlair 
                            ? 'text.primary' 
                            : 'grey.300',
                        borderRadius: 2,
                        p: 1,
                        height: 140,
                        width: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: activeSlot === 'secondary' ? 'success.dark' : 'primary.main',
                        },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontSize: '0.7rem' }}>
                        Secondary Flair
                      </Typography>
                      {secondaryFlair ? (
                        <>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 0.5,
                            }}
                          >
                            <img
                              src={getImageUrl(secondaryFlair.image_source)}
                              alt={secondaryFlair.text}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              textAlign: 'center',
                              fontSize: '0.65rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              minHeight: '2.4em',
                              width: '100%',
                            }}
                          >
                            {secondaryFlair.text}
                          </Typography>
                        </>
                      ) : (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            minHeight: '2.4em', 
                            display: 'flex', 
                            alignItems: 'center',
                            textAlign: 'center',
                            fontSize: '0.65rem',
                            width: '100%',
                          }}
                        >
                          No flair selected
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                
                {/* Protected Text Input */}
                {userFlairData?.protect_text === 1 && (
                  <Box sx={{ mb: 2, px: 1 }}>
                    <TextField
                      fullWidth
                      label="Custom Flair Text"
                      value={protectedFlairText}
                      onChange={(e) => {
                        const textWithoutEmojis = e.target.value;
                        // Calculate emojis that will be prepended
                        const emojis: string[] = [];
                        if (primaryFlair?.flair_code) {
                          emojis.push(`:${primaryFlair.flair_code}:`);
                        }
                        if (secondaryFlair?.flair_code) {
                          emojis.push(`:${secondaryFlair.flair_code}:`);
                        }
                        // Emojis are joined with spaces: ":emoji1: :emoji2:"
                        const emojiString = emojis.join(' ');
                        const fullText = emojiString + (textWithoutEmojis ? ' ' + textWithoutEmojis : '');
                        // Validate 64 character limit
                        if (fullText.length <= 64) {
                          setProtectedFlairText(textWithoutEmojis);
                        }
                      }}
                      inputProps={{ maxLength: 64 }}
                      helperText={(() => {
                        const emojis: string[] = [];
                        if (primaryFlair?.flair_code) {
                          emojis.push(`:${primaryFlair.flair_code}:`);
                        }
                        if (secondaryFlair?.flair_code) {
                          emojis.push(`:${secondaryFlair.flair_code}:`);
                        }
                        // Emojis are joined with spaces: ":emoji1: :emoji2:"
                        const emojiString = emojis.join(' ');
                        const totalLength = emojiString.length + (protectedFlairText ? protectedFlairText.length + 1 : 0);
                        return `${protectedFlairText.length}/64 (text only). Total with emojis: ${totalLength}/64`;
                      })()}
                      error={(() => {
                        const emojis: string[] = [];
                        if (primaryFlair?.flair_code) {
                          emojis.push(`:${primaryFlair.flair_code}:`);
                        }
                        if (secondaryFlair?.flair_code) {
                          emojis.push(`:${secondaryFlair.flair_code}:`);
                        }
                        // Emojis are joined with spaces: ":emoji1: :emoji2:"
                        const emojiString = emojis.join(' ');
                        const totalLength = emojiString.length + (protectedFlairText ? protectedFlairText.length + 1 : 0);
                        return totalLength > 64;
                      })()}
                      size="small"
                    />
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateFlair}
                    disabled={isUpdating || (!primaryFlair && !secondaryFlair)}
                    startIcon={<RefreshIcon />}
                  >
                    {isUpdating ? 'Updating...' : 'Update Flair'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFlair}
                    disabled={!primaryFlair && !secondaryFlair}
                    startIcon={<ClearIcon />}
                  >
                    Reset Flair
                  </Button>
                </Box>
              </Paper>
              </>
            )}

            <Dialog open={siblingDialogOpen} onClose={() => !isApplyingSiblings && setSiblingDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Update your flair in other communities?</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  We found the same flair code or label on other subs you use. Choose where to apply your selection (no
                  custom text is copied; each sub uses its own template text).
                </Typography>
                <List dense disablePadding>
                  {siblingTargets.map((t) => (
                    <ListItem key={t.subreddit} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <FormControlLabel
                        sx={{ mr: 0, width: '100%', alignItems: 'center' }}
                        control={
                          <Checkbox
                            checked={!!siblingChecked[t.subreddit]}
                            onChange={(e) =>
                              setSiblingChecked((prev) => ({ ...prev, [t.subreddit]: e.target.checked }))
                            }
                            disabled={isApplyingSiblings}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 0.5 }}>
                            <Box
                              component="img"
                              src={
                                t.logoUrl ||
                                `${import.meta.env.VITE_CDN_BASE_URL || 'https://cdn.redditcfb.com'}/${import.meta.env.VITE_CDN_IMAGE_PATH || '60x60'}/${t.cdn_sub || flairDataSourceSub || t.subreddit}/placeholder.png`
                              }
                              alt=""
                              sx={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.visibility = 'hidden';
                              }}
                            />
                            <Typography variant="body1">{t.displayName}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </DialogContent>
              <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isApplyingSiblings || !siblingTargets.some((t) => siblingChecked[t.subreddit])}
                  onClick={handleApplySiblingFlairs}
                >
                  {isApplyingSiblings ? 'Applying…' : 'Add Flair'}
                </Button>
                <Button size="small" onClick={() => setSiblingDialogOpen(false)} disabled={isApplyingSiblings}>
                  Skip
                </Button>
              </DialogActions>
            </Dialog>

            {!user && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You are browsing without an account — search and explore flairs below. Use{' '}
                <strong>Login with Reddit</strong> in the header to choose and save your flair.
              </Alert>
            )}

              {/* Popular and Trending */}
              {!isLoadingFlair && flairTemplates.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Paper sx={{ p: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', mb: 1 }}>
                      Popular
                    </Typography>
                    <Box sx={POPULAR_TRENDING_GRID_SX}>
                      {[...flairTemplates]
                        .sort(comparePopularFlairs)
                        .slice(0, 12)
                        .map((flair) => {
                          const isAward = flair.is_award === true;
                          const hasEarned = flair.has_earned !== false;
                          const isDisabled = isAward && !hasEarned;
                          const isDupOther = isFlairBlockedDuplicate(flair);
                          const isClickable = canSelectFlair && !isDisabled && !isDupOther;

                          const flairBox = (
                            <Box
                              onClick={() => isClickable && handleFlairClick(flair)}
                              sx={{
                                cursor: isClickable ? 'pointer' : isDisabled || isDupOther ? 'not-allowed' : 'default',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 0.5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                position: 'relative',
                                height: 140,
                                width: '100%',
                                maxWidth: 120,
                                mx: 'auto',
                                opacity: isDisabled || isDupOther ? 0.45 : 1,
                                backgroundColor: isDisabled ? 'action.disabledBackground' : 'transparent',
                                '&:hover': isClickable
                                  ? {
                                      borderColor: 'primary.main',
                                      boxShadow: 1,
                                    }
                                  : {},
                              }}
                            >
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                fontSize: '0.55rem',
                                color: 'text.secondary',
                                fontWeight: 'bold',
                              }}
                            >
                              {flair.user_count !== undefined 
                                ? flair.user_count >= 1000 
                                  ? `${(flair.user_count / 1000).toFixed(1)}K`.replace('.0', '')
                                  : flair.user_count.toString()
                                : '0'}
                            </Typography>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 0.5,
                                mt: 1,
                              }}
                            >
                              <img
                                src={getImageUrl(flair.image_source)}
                                alt={flair.text}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                textAlign: 'center',
                                fontSize: '0.65rem',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                minHeight: '2.4em',
                                width: '100%',
                              }}
                            >
                              {flair.text}
                            </Typography>
                          </Box>
                          );

                          return (
                            <Tooltip
                              key={flair.id}
                              title={
                                isDisabled
                                  ? 'This is an award flair, ask the mod team how to earn it'
                                  : isDupOther
                                    ? 'Same flair as your other slot (different image). Pick a different second flair.'
                                    : !canSelectFlair
                                      ? 'Log in with Reddit to select this flair'
                                      : ''
                              }
                              arrow
                            >
                              {flairBox}
                            </Tooltip>
                          );
                        })}
                    </Box>
                  </Paper>
                  <Paper sx={{ p: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontSize: '0.9rem' }}>
                        Trending Now
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => navigateOrExternal(navigate, trendsPath(canonicalSubredditName))}
                        sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                      >
                        View Trends
                      </Button>
                    </Box>
                    {isLoadingTrending ? (
                      <CircularProgress size={20} />
                    ) : trendingFlairs.length > 0 ? (
                      <Box sx={POPULAR_TRENDING_GRID_SX}>
                        {trendingFlairs.map((flair) => {
                          const flairTemplate = flairTemplates.find(t => t.id === flair.id) || flair;
                          const isAward = flairTemplate.is_award === true;
                          const hasEarned = flairTemplate.has_earned !== false;
                          const isDisabled = isAward && !hasEarned;
                          const isDupOther = isFlairBlockedDuplicate(flairTemplate);
                          const isClickable = canSelectFlair && !isDisabled && !isDupOther;

                          const flairBox = (
                            <Box
                              onClick={() => isClickable && handleFlairClick(flairTemplate)}
                              sx={{
                                cursor: isClickable ? 'pointer' : isDisabled || isDupOther ? 'not-allowed' : 'default',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 0.5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                position: 'relative',
                                height: 140,
                                width: '100%',
                                maxWidth: 120,
                                mx: 'auto',
                                opacity: isDisabled || isDupOther ? 0.45 : 1,
                                backgroundColor: isDisabled ? 'action.disabledBackground' : 'transparent',
                                '&:hover': isClickable
                                  ? {
                                      borderColor: 'primary.main',
                                      boxShadow: 1,
                                    }
                                  : {},
                              }}
                            >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    fontSize: '0.55rem',
                                    color: flair.percent_change >= 0 ? 'success.main' : 'error.main',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {flair.percent_change >= 0 ? '+' : ''}{flair.percent_change.toFixed(1)}%
                                </Typography>
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 0.5,
                                    mt: 1,
                                  }}
                                >
                                  <img
                                    src={getImageUrl(flair.image_source || flairTemplate?.image_source)}
                                    alt={flair.text}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textAlign: 'center',
                                    fontSize: '0.65rem',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    minHeight: '2.4em',
                                    width: '100%',
                                  }}
                                >
                                  {flair.text}
                                </Typography>
                              </Box>
                          );

                          return (
                            <Tooltip
                              key={flair.id}
                              title={
                                isDisabled
                                  ? 'This is an award flair, ask the mod team how to earn it'
                                  : isDupOther
                                    ? 'Same flair as your other slot (different image). Pick a different second flair.'
                                    : !canSelectFlair
                                      ? 'Log in with Reddit to select this flair'
                                      : ''
                              }
                              arrow
                            >
                              {flairBox}
                            </Tooltip>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No trending data available
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              {!isLoadingFlair && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="Search by flair name, abbreviation, image label, or file stem…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Paper>
              )}

              {isLoadingFlair ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Accordion
                    defaultExpanded={!isNarrow}
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        Nested Filter
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                          <Chip
                            label="All"
                            onClick={() => handleNavigationClick('class', null)}
                            sx={{ cursor: 'pointer' }}
                            color={!selectedClass ? 'primary' : 'default'}
                          />
                          {selectedClass && (
                            <>
                              <Chip
                                label={selectedClass}
                                onClick={() => handleNavigationClick('class', selectedClass)}
                                sx={{ cursor: 'pointer' }}
                                color="primary"
                              />
                              {selectedFamily && selectedFamily !== selectedClass && (
                                <Chip
                                  label={selectedFamily}
                                  onClick={() => handleNavigationClick('family', selectedFamily)}
                                  sx={{ cursor: 'pointer' }}
                                  color="primary"
                                />
                              )}
                              {selectedGenus && selectedGenus !== selectedFamily && selectedGenus !== selectedClass && (
                                <Chip
                                  label={selectedGenus}
                                  onClick={() => handleNavigationClick('genus', selectedGenus)}
                                  sx={{ cursor: 'pointer' }}
                                  color="primary"
                                />
                              )}
                            </>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {!selectedClass && classes.length > 1 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Classes
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {classes.map((classItem) => (
                                  <Chip
                                    key={classItem.name}
                                    label={classItem.name}
                                    onClick={() => handleNavigationClick('class', classItem.name)}
                                    sx={{ cursor: 'pointer' }}
                                    icon={classItem.image ? (
                                      <img
                                        src={getImageUrl(classItem.image)}
                                        alt={classItem.name}
                                        style={{ width: 18, height: 18, objectFit: 'contain' }}
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                      />
                                    ) : undefined}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {selectedClass && !selectedFamily && families.length > 1 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Conferences / families
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {families.map((familyItem) => (
                                  <Chip
                                    key={familyItem.name}
                                    label={familyItem.name}
                                    onClick={() => handleNavigationClick('family', familyItem.name)}
                                    sx={{ cursor: 'pointer' }}
                                    icon={familyItem.image ? (
                                      <img
                                        src={getImageUrl(familyItem.image)}
                                        alt={familyItem.name}
                                        style={{ width: 18, height: 18, objectFit: 'contain' }}
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                      />
                                    ) : undefined}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>

                  {/* Genus chips above the grid once class + conference are chosen (scoped list) */}
                  {selectedClass && selectedFamily && genuses.length > 0 && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Genus
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {genuses.map((genusItem) => (
                          <Chip
                            key={genusItem.name}
                            label={genusItem.name}
                            onClick={() => handleNavigationClick('genus', genusItem.name)}
                            color={selectedGenus === genusItem.name ? 'primary' : 'default'}
                            sx={{ cursor: 'pointer' }}
                            icon={genusItem.image ? (
                              <img
                                src={getImageUrl(genusItem.image)}
                                alt={genusItem.name}
                                style={{ width: 18, height: 18, objectFit: 'contain' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : undefined}
                          />
                        ))}
                      </Box>
                    </Paper>
                  )}

                  {/* Flair Grid */}
                  {filteredFlairs.length === 0 ? (
                    <Card>
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          No Flairs Found
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {searchQuery
                            ? 'Try a different search term.'
                            : 'Use search above this section, then Nested Filter for class and conference, then choose a genus when shown.'}
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      <Box sx={MAIN_FLAIR_GRID_SX}>
                        {paginatedFlairs.map((flair) => {
                              const isPrimary = primaryFlair?.id === flair.id;
                              const isSecondary = secondaryFlair?.id === flair.id;
                              const isSelected = isPrimary || isSecondary;
                              const isAward = flair.is_award === true;
                              const hasEarned = flair.has_earned !== false; // Default to true if not set
                              const isDisabled = isAward && !hasEarned;
                              const isDupOther = isFlairBlockedDuplicate(flair);
                              const isClickable = canSelectFlair && !isDisabled && !isDupOther;

                              const flairCard = (
                                <Card
                                  sx={{
                                    cursor: isClickable ? 'pointer' : isDisabled || isDupOther ? 'not-allowed' : 'default',
                                    border: isSelected ? '2px solid' : '1px solid',
                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                    backgroundColor: isSelected ? 'action.selected' : (isDisabled ? 'action.disabledBackground' : 'background.paper'),
                                    opacity: isDisabled || isDupOther ? 0.45 : 1,
                                    height: 140,
                                    width: '100%',
                                    maxWidth: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    mx: 'auto',
                                    '&:hover': isClickable
                                      ? {
                                          borderColor: 'primary.main',
                                          boxShadow: 2,
                                        }
                                      : {},
                                  }}
                                  onClick={() => isClickable && handleFlairClick(flair)}
                                >
                                <CardContent sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, height: '100%' }}>
                                  <Box
                                    sx={{
                                      width: 60,
                                      height: 60,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      mb: 0.5,
                                    }}
                                  >
                                    <img
                                      src={getImageUrl(flair.image_source)}
                                      alt={flair.text}
                                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      textAlign: 'center',
                                      fontSize: '0.65rem',
                                      lineHeight: 1.2,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      minHeight: '2.4em',
                                    }}
                                  >
                                    {flair.text}
                                  </Typography>
                                  {isSelected && (
                                    <Chip
                                      label={isPrimary ? 'Primary' : 'Secondary'}
                                      size="small"
                                      color="primary"
                                      sx={{ mt: 0.5, height: 16, fontSize: '0.6rem' }}
                                    />
                                  )}
                                </CardContent>
                                  </Card>
                              );

                              return (
                                <Tooltip
                                  key={flair.id}
                                  title={
                                    isDisabled
                                      ? 'This is an award flair, ask the mod team how to earn it'
                                      : isDupOther
                                        ? 'Same flair as your other slot (different image). Pick a different second flair.'
                                        : !canSelectFlair
                                          ? 'Log in with Reddit to select this flair'
                                          : ''
                                  }
                                  arrow
                                >
                                  {flairCard}
                                </Tooltip>
                              );
                            })}
                      </Box>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <Typography variant="body2">
                            Page {currentPage} of {totalPages} ({filteredFlairs.length} total)
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </>
        </Container>
      </Box>
    </Box>
  );
};

export default SubredditPage;
